/**
 * components/scanner/ScannerSidebar.tsx
 * ─────────────────────────────────────────────────────────────
 * Pre-market scanner sidebar with clear feedback states:
 *  - Idle (never scanned)
 *  - Loading / scanning
 *  - After hours / market closed (with next open time)
 *  - Zero results (with reason)
 *  - Results list with options qualification badges
 * ─────────────────────────────────────────────────────────────
 */

import { useState }                  from 'react';
import {
  RefreshCw, TrendingUp, AlertCircle,
  ChevronDown, ChevronUp, Loader2,
  Clock, CheckCircle, Moon
} from 'lucide-react';
import clsx                          from 'clsx';
import { useScanner, useTicker, useAccount } from '../../store';
import { scannerApi }                from '../../lib/api';
import { useTickerAnalysis }         from '../../hooks/useTickerAnalysis';
import { useOptionsQualifier }       from '../../hooks/useOptionsQualifier';
import { qualSummaryLabel }          from '../../lib/optionsQual';
import type { Gapper, OptionsQualCheck } from '../../types';

// ── Market session helper ──────────────────────────────────────
type SessionState = 'pre-market' | 'open' | 'after-hours' | 'closed' | 'weekend';

function getSessionState(): { state: SessionState; label: string; nextEvent: string } {
  const et   = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day  = et.getDay(); // 0=Sun, 6=Sat
  const h    = et.getHours();
  const m    = et.getMinutes();
  const mins = h * 60 + m;

  if (day === 0 || day === 6) {
    const daysUntilMon = day === 6 ? 2 : 1;
    return {
      state:     'weekend',
      label:     'Weekend',
      nextEvent: `Pre-market opens Monday 4:00 AM ET (in ~${daysUntilMon * 24 - h}h)`,
    };
  }

  if (mins >= 240 && mins < 570)  return { state: 'pre-market', label: 'Pre-Market', nextEvent: 'Market opens 9:30 AM ET' };
  if (mins >= 570 && mins < 960)  return { state: 'open',       label: 'Market Open', nextEvent: 'Market closes 4:00 PM ET' };
  if (mins >= 960 && mins < 1200) return { state: 'after-hours',label: 'After Hours', nextEvent: 'Pre-market opens 4:00 AM ET tomorrow' };

  return {
    state:     'closed',
    label:     'Closed',
    nextEvent: mins < 240 ? `Pre-market opens in ${240 - mins} min` : 'Pre-market opens 4:00 AM ET tomorrow',
  };
}

// ── Constants ─────────────────────────────────────────────────

const CATALYST_CONFIG: Record<string, { textCls: string; borderCls: string; bgCls: string }> = {
  earnings: { textCls: 'text-bull',   borderCls: 'border-bull/20',   bgCls: 'bg-bull/8'    },
  fda:      { textCls: 'text-violet', borderCls: 'border-violet/25', bgCls: 'bg-violet/10' },
  upgrade:  { textCls: 'text-sky',    borderCls: 'border-sky/20',    bgCls: 'bg-sky/8'     },
  contract: { textCls: 'text-sky',    borderCls: 'border-sky/20',    bgCls: 'bg-sky/8'     },
  macro:    { textCls: 'text-amber',  borderCls: 'border-amber/20',  bgCls: 'bg-amber/8'   },
  social:   { textCls: 'text-amber',  borderCls: 'border-amber/20',  bgCls: 'bg-amber/8'   },
  unknown:  { textCls: 'text-amber',  borderCls: 'border-amber/20',  bgCls: 'bg-amber/8'   },
};

const SCORE_COLOR = (s: number) =>
  s >= 70 ? 'text-bull bg-bull/10 border-bull/20'
  : s >= 50 ? 'text-amber bg-amber/10 border-amber/20'
  : 'text-bear bg-bear/10 border-bear/20';


// ── Options Qual Badge ─────────────────────────────────────────

function QualBadge({ check }: { check: OptionsQualCheck | undefined }) {
  if (!check) return null;
  if (check.status === 'loading') {
    return (
      <span className="flex items-center gap-1 text-ink-3">
        <Loader2 size={9} className="animate-spin" />
        <span className="text-2xs font-mono">Checking…</span>
      </span>
    );
  }
  if (check.status === 'error') return null;

  const cfg = {
    pass: 'bg-bull/10 text-bull border-bull/20',
    warn: 'bg-amber/10 text-amber border-amber/20',
    fail: 'bg-bear/10 text-bear border-bear/20',
  }[check.status] ?? 'bg-bg-3 text-ink-3 border-border';

  const label = {
    pass: 'Options ✓',
    warn: 'Options ⚠',
    fail: 'Options ✗',
  }[check.status] ?? '—';

  return (
    <span className={clsx('text-2xs font-mono px-1.5 py-0.5 rounded border', cfg)}>
      {label}
    </span>
  );
}


// ── Options Checklist ──────────────────────────────────────────

function RuleRow({ label, passed, value, hardFail = false }: {
  label: string; passed: boolean; value: string; hardFail?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-1.5">
        <span className={clsx(
          'w-3 h-3 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0',
          passed ? 'bg-bull/15 text-bull' : hardFail ? 'bg-bear/20 text-bear' : 'bg-amber/15 text-amber'
        )}>
          {passed ? '✓' : hardFail ? '✗' : '!'}
        </span>
        <span className="text-2xs text-ink-2">{label}</span>
      </div>
      <span className={clsx('text-2xs font-mono', passed ? 'text-bull' : hardFail ? 'text-bear' : 'text-amber')}>
        {value}
      </span>
    </div>
  );
}

function OptionsChecklist({ check, accountBalance }: {
  check: OptionsQualCheck; accountBalance: number;
}) {
  if (check.status === 'loading') {
    return (
      <div className="px-3 pb-2">
        <div className="bg-bg-2 rounded-lg p-3 flex items-center gap-2 text-2xs text-ink-3">
          <Loader2 size={11} className="animate-spin" />
          Fetching options chain…
        </div>
      </div>
    );
  }
  if (check.status === 'error') {
    return (
      <div className="px-3 pb-2">
        <div className="bg-bg-2 rounded-lg p-2 text-2xs text-ink-3">
          Options data unavailable — verify TRADIER_TOKEN in .env
        </div>
      </div>
    );
  }

  const maxRisk   = accountBalance * 0.02;
  const contracts = check.budgetVal > 0
    ? Math.max(1, Math.floor(maxRisk / (check.budgetVal * 100 * 0.5))) : 0;

  const statusCfg = {
    pass: { banner: 'bg-bull/8 border-bull/20 text-bull',    icon: '✓', title: 'All rules pass' },
    warn: { banner: 'bg-amber/8 border-amber/20 text-amber', icon: '⚠', title: `${check.passCount}/5 rules — use caution` },
    fail: { banner: 'bg-bear/8 border-bear/20 text-bear',    icon: '✗', title: `${check.passCount}/5 rules — avoid` },
  }[check.status];

  return (
    <div className="px-3 pb-3">
      <div className="bg-bg-2 rounded-lg overflow-hidden border border-border">
        <div className={clsx('flex items-center gap-2 px-3 py-2 text-2xs border-b', statusCfg.banner)}>
          <span className="font-bold">{statusCfg.icon}</span>
          <span className="font-medium">
            {check.bestStrike ? `$${check.bestStrike}C` : 'No strike'}
            {check.bestExpiry ? ` · ${check.bestExpiry}` : ''}
          </span>
          <span className="opacity-70 ml-auto">{statusCfg.title}</span>
        </div>
        <div className="px-3 py-2">
          <RuleRow label="Bid/Ask spread" passed={check.spreadOk}
            value={`$${check.spreadVal.toFixed(2)} ${check.spreadOk ? '≤ $0.15 ✓' : '> $0.15'}`}
            hardFail={check.spreadVal > 0.25} />
          <RuleRow label="Open interest" passed={check.oiOk}
            value={`${check.oiVal.toLocaleString()} ${check.oiOk ? '≥ 500 ✓' : '< 500'}`}
            hardFail={check.oiVal < 100} />
          <RuleRow label="Delta (0.25–0.45)" passed={check.deltaOk}
            value={`${check.deltaVal.toFixed(2)} ${check.deltaOk ? '✓' : '— out of zone'}`} />
          <RuleRow label={`Budget ($${maxRisk.toFixed(0)} risk)`} passed={check.budgetOk}
            value={`$${(check.budgetVal * 100).toFixed(0)}/contract → ${contracts}x`} />
          <RuleRow label="Overall liquidity" passed={check.liquidOk}
            value={check.liquidOk ? 'Liquid ✓' : 'Thin market'} />
        </div>
        {check.status === 'fail' && check.failReasons.length > 0 && (
          <div className="px-3 py-2 border-t border-border bg-bear/5 text-2xs text-bear">
            {check.failReasons.map((r, i) => <div key={i}>✗ {r}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Gapper Card ────────────────────────────────────────────────

function GapperCard({ g, isSelected, check, accountBalance, onSelect }: {
  g: Gapper;
  isSelected: boolean;
  check: OptionsQualCheck | undefined;
  accountBalance: number;
  onSelect: (g: Gapper) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const cat = CATALYST_CONFIG[g.catalystType] ?? CATALYST_CONFIG.unknown;
  const hasRealCatalyst = !g.enriching && g.catalystType !== 'unknown' && g.catalystSummary !== 'No recent news found' && g.catalystSummary !== 'Run analysis for catalyst details';

  // Catalyst type label
  const catalystLabel: Record<string, string> = {
    earnings: 'EARNINGS', fda: 'FDA', upgrade: 'UPGRADE',
    contract: 'CONTRACT', macro: 'MACRO', social: 'SOCIAL', unknown: 'NEWS',
  };

  return (
    <div className={clsx(
      'border-l-2 transition-colors',
      isSelected ? 'border-l-bull bg-bg-2' : 'border-l-transparent hover:bg-bg-2/50'
    )}>
      {/* Catalyst banner */}
      <div className={clsx(
        'mx-3 mt-2 px-2 py-2 rounded-t-md border-x border-t',
        cat.textCls, cat.borderCls, cat.bgCls
      )}>
        {/* Type badge + summary */}
        <div className="flex items-start gap-1.5">
          <span className={clsx(
            'flex-shrink-0 text-[9px] font-mono font-bold px-1 py-0.5 rounded',
            'border tracking-wider mt-0.5',
            cat.textCls, cat.borderCls,
            hasRealCatalyst ? 'opacity-100' : 'opacity-50'
          )}>
            {catalystLabel[g.catalystType] ?? 'NEWS'}
          </span>
          <span className="text-2xs leading-snug">
            {g.enriching
              ? <span className="opacity-50 italic">Scanning for catalyst…</span>
              : hasRealCatalyst
              ? (g.catalystSummary.length > 65
                  ? g.catalystSummary.slice(0, 62) + '…'
                  : g.catalystSummary)
              : <span className="opacity-50 italic">No catalyst found — verify manually</span>
            }
          </span>
        </div>
      </div>

      {/* Ticker row */}
      <div className="flex items-stretch">
        <button
          onClick={() => onSelect(g)}
          className="flex-1 flex items-center justify-between px-3 py-2 text-left min-w-0"
        >
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className={clsx('text-sm font-medium', isSelected && 'text-bull')}>{g.sym}</span>
            <span className="text-2xs text-ink-3 truncate">{g.name}</span>
            <div className="mt-0.5"><QualBadge check={check} /></div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
            <span className="text-xs font-mono text-bull">+{g.changePct.toFixed(1)}%</span>
            <span className="text-2xs text-ink-3 font-mono">{g.rvol}× vol</span>
            {hasRealCatalyst && (
              <span className="flex items-center gap-1 text-2xs font-mono text-bull/70">
                <span className="w-1.5 h-1.5 rounded-full bg-bull/60" />
                catalyst
              </span>
            )}
            {g.enriching ? (
              <span className="flex items-center gap-1 text-2xs font-mono px-1.5 py-0.5 rounded border border-border text-ink-3">
                <Loader2 size={9} className="animate-spin" />
                scoring…
              </span>
            ) : (
              <span className={clsx('text-2xs font-mono px-1.5 py-0.5 rounded border', SCORE_COLOR(g.score))}>
                {g.score}
              </span>
            )}
            {g.scoreBreakdown && g.scoreBreakdown.evaluatedFactors < g.scoreBreakdown.totalFactors && (
              <span
                className="text-2xs text-ink-3 font-mono"
                title="Some scoring factors couldn't be evaluated (missing data) — expand for details"
              >
                {g.scoreBreakdown.evaluatedFactors}/{g.scoreBreakdown.totalFactors} scored
              </span>
            )}
          </div>
        </button>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          className={clsx(
            'flex items-center px-2 border-l border-border/50',
            'text-ink-3 hover:text-ink-1 hover:bg-bg-3 transition-colors',
            expanded && 'bg-bg-3 text-ink-1'
          )}
          title={expanded ? 'Hide options rules' : 'Show options rules'}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {/* Options checklist */}
      {expanded && check && (
        <OptionsChecklist check={check} accountBalance={accountBalance} />
      )}
      {expanded && !check && (
        <div className="px-3 pb-2">
          <div className="bg-bg-2 rounded-lg p-3 text-2xs text-ink-3">
            Options check pending…
          </div>
        </div>
      )}
    </div>
  );
}


// ── Empty / Status States ──────────────────────────────────────

function ScanStatus({ state, nextEvent }: { state: SessionState; nextEvent: string }) {
  const cfg = {
    'weekend':     { icon: Moon,         color: 'text-ink-3',  title: 'Weekend',      sub: nextEvent },
    'closed':      { icon: Moon,         color: 'text-ink-3',  title: 'Market Closed', sub: nextEvent },
    'after-hours': { icon: Clock,        color: 'text-sky',    title: 'After Hours',  sub: nextEvent },
    'pre-market':  { icon: TrendingUp,   color: 'text-amber',  title: 'Pre-Market',   sub: 'Good time to scan for gaps' },
    'open':        { icon: TrendingUp,   color: 'text-bull',   title: 'Market Open',  sub: 'Scan for active movers' },
  }[state];

  const Icon = cfg.icon;

  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-4 text-center">
      <Icon size={28} className={clsx('opacity-40', cfg.color)} />
      <div>
        <div className={clsx('text-sm font-medium font-mono mb-1', cfg.color)}>{cfg.title}</div>
        <div className="text-2xs text-ink-3 leading-relaxed">{cfg.sub}</div>
      </div>
    </div>
  );
}


// ── Main ScannerSidebar ────────────────────────────────────────

export function ScannerSidebar() {
  const scanner = useScanner();
  const ticker  = useTicker();
  const account = useAccount();
  const { analyze } = useTickerAnalysis();

  // Track whether a scan has been attempted this session
  const [scanAttempted, setScanAttempted] = useState(false);
  const [lastScanSession, setLastScanSession] = useState<SessionState | null>(null);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'high' | 'options' | 'catalyst'>('all');
  const [minScoreFilter, setMinScoreFilter] = useState(0);

  useOptionsQualifier();

  const accountBalance = account.stats?.balance ?? 2000;
  const session = getSessionState();

  // Catalyst summary counts
  const catalystCount = scanner.gappers.filter(
    g => g.catalystType !== 'unknown' &&
         g.catalystSummary !== 'No recent news found' &&
         g.catalystSummary !== 'Run analysis for catalyst details'
  ).length;

  // Qual summary counts
  const qualCounts = Object.values(scanner.optionsChecks).reduce(
    (acc, c) => {
      if (c.status === 'pass')    acc.pass++;
      else if (c.status === 'warn') acc.warn++;
      else if (c.status === 'fail') acc.fail++;
      else if (c.status === 'loading') acc.loading++;
      return acc;
    },
    { pass: 0, warn: 0, fail: 0, loading: 0 }
  );

  async function runScan() {
    if (scanner.isScanning) return;
    scanner.setScanning(true);
    scanner.setScanError(null);
    scanner.clearOptionsChecks();
    setScanAttempted(true);
    setLastScanSession(session.state);

    try {
      // Resolves quickly with the raw (unscored) gapper list — full scoring
      // continues in the background and streams in per-ticker over the
      // WebSocket ('scanner-update', handled in useWebSocket.ts), which also
      // flips isScanning off via 'scan-complete'/'scan-error' when it's done.
      const gappers = await scannerApi.run();
      scanner.setGappers(gappers);
      if (gappers.length === 0) {
        // Nothing matched the filter — no background enrichment will run,
        // so there's no 'scan-complete' event coming to end the spinner.
        scanner.setScanning(false);
        scanner.setLastScan(Date.now());
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Scan failed';
      scanner.setScanError(msg);
      scanner.setScanning(false);
    }
  }

  // ── Filter logic ─────────────────────────────────────────────
  const filteredGappers = scanner.gappers.filter(g => {
    if (activeFilter === 'high')     return g.score >= 70;
    if (activeFilter === 'options')  return scanner.optionsChecks[g.sym]?.status === 'pass';
    if (activeFilter === 'catalyst') return g.catalystType !== 'unknown' &&
                                            g.catalystSummary !== 'No recent news found';
    if (minScoreFilter > 0)          return g.score >= minScoreFilter;
    return true;
  });

  // ── Render the main content area ────────────────────────────
  function renderContent() {
    // Actively scanning, and Step 1 (the fast raw-gapper fetch) hasn't
    // returned yet — once gappers exist, show the list below instead (each
    // card shows its own "scoring…" state while background enrichment
    // streams in over the WebSocket).
    if (scanner.isScanning && scanner.gappers.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-4 text-center">
          <RefreshCw size={28} className="text-bull/60 animate-spin" />
          <div>
            <div className="text-sm font-medium text-ink-2 mb-1">Scanning…</div>
            <div className="text-2xs text-ink-3 leading-relaxed">
              Fetching pre-market data from Alpaca<br />and computing probability scores
            </div>
          </div>
          <div className="flex gap-1">
            {[0, 0.2, 0.4].map((d, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-bull/40 animate-pulse"
                   style={{ animationDelay: `${d}s` }} />
            ))}
          </div>
        </div>
      );
    }

    // Error
    if (scanner.error) {
      return (
        <div className="p-4">
          <div className="bg-bear/10 border border-bear/20 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle size={14} className="text-bear flex-shrink-0 mt-0.5" />
              <div className="text-xs font-medium text-bear">Scan Failed</div>
            </div>
            <div className="text-2xs text-bear/80 leading-relaxed mb-3">{scanner.error}</div>
            <button onClick={runScan} className="text-2xs font-mono text-bear hover:text-bear/80 underline">
              Try again →
            </button>
          </div>
        </div>
      );
    }

    // Has results
    if (scanner.gappers.length > 0) {
      return (
        <div className="divide-y divide-border/40">
          {filteredGappers.map(g => (
            <GapperCard
              key={g.sym}
              g={g}
              isSelected={ticker.sym === g.sym}
              check={scanner.optionsChecks[g.sym]}
              accountBalance={accountBalance}
              onSelect={g => analyze(g.sym, g)}
            />
          ))}
        </div>
      );
    }

    // Scan ran but zero results
    if (scanAttempted) {
      const isOffHours = lastScanSession === 'closed' ||
                         lastScanSession === 'after-hours' ||
                         lastScanSession === 'weekend';
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-4 text-center">
          <CheckCircle size={28} className="text-bull/40" />
          <div>
            <div className="text-sm font-medium text-ink-2 mb-2">
              Scan complete — no gappers found
            </div>
            <div className="text-2xs text-ink-3 leading-relaxed space-y-1.5">
              {isOffHours ? (
                <>
                  <p>No pre-market activity right now.</p>
                  <p className="text-amber">
                    Best scan time: <b>4:00–9:15 AM ET</b> on weekdays when stocks are actively gapping on news.
                  </p>
                </>
              ) : (
                <>
                  <p>No stocks currently meet all criteria:</p>
                  <div className="text-left bg-bg-2 border border-border rounded-md p-2 mt-2 space-y-1">
                    <div className="flex justify-between">
                      <span>Gap ≥ 3%</span>
                      <span className="text-ink-2">not met</span>
                    </div>
                    <div className="flex justify-between">
                      <span>RVOL ≥ 1.5×</span>
                      <span className="text-ink-2">not met</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Price $10–$500</span>
                      <span className="text-ink-2">not met</span>
                    </div>
                  </div>
                  <p className="pt-1">Try scanning again closer to market open.</p>
                </>
              )}
            </div>
          </div>
          <button
            onClick={runScan}
            className="text-2xs font-mono text-bull/60 hover:text-bull border border-bull/20 hover:border-bull/40 rounded px-3 py-1.5 transition-colors"
          >
            Scan again
          </button>
        </div>
      );
    }

    // Never scanned — show session context
    return <ScanStatus state={session.state} nextEvent={session.nextEvent} />;
  }

  return (
    <aside className="w-full h-full bg-bg-1 md:border-r border-border flex flex-col overflow-hidden">

      {/* Header */}
      <div className="p-3 border-b border-border space-y-2">
        <div className="flex items-center justify-between">
          <span className="section-label mb-0">Pre-Market Scanner</span>
          {/* Live session indicator */}
          <span className={clsx('text-2xs font-mono flex items-center gap-1',
            session.state === 'pre-market' ? 'text-amber' :
            session.state === 'open'       ? 'text-bull'  :
            'text-ink-3'
          )}>
            <span className={clsx('w-1.5 h-1.5 rounded-full',
              session.state === 'pre-market' ? 'bg-amber animate-pulse' :
              session.state === 'open'       ? 'bg-bull animate-pulse'  :
              'bg-ink-3'
            )} />
            {session.label}
          </span>
        </div>

        <button
          onClick={runScan}
          disabled={scanner.isScanning}
          className={clsx(
            'w-full flex items-center justify-center gap-2 py-2 rounded-md border',
            'text-xs font-mono font-medium transition-all',
            scanner.isScanning
              ? 'bg-bg-2 border-border text-ink-3 cursor-wait'
              : 'bg-bull/8 border-bull/30 text-bull hover:bg-bull/15 active:scale-[0.99]'
          )}
        >
          <RefreshCw size={12} className={scanner.isScanning ? 'animate-spin' : ''} />
          {scanner.isScanning ? 'Scanning…' : 'Scan Gappers'}
        </button>

        {/* Quick filters — show when results exist */}
        {scanner.gappers.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex gap-1 flex-wrap">
              {([
                { id: 'all',      label: 'All',       count: scanner.gappers.length },
                { id: 'high',     label: '≥70 Score', count: scanner.gappers.filter(g => g.score >= 70).length },
                { id: 'options',  label: 'Opt ✓',     count: Object.values(scanner.optionsChecks).filter(c => c.status === 'pass').length },
                { id: 'catalyst', label: 'Catalyst',  count: scanner.gappers.filter(g => g.catalystType !== 'unknown' && g.catalystSummary !== 'No recent news found').length },
              ] as const).map(({ id, label, count }) => (
                <button key={id}
                  onClick={() => { setActiveFilter(id as typeof activeFilter); setMinScoreFilter(0); }}
                  className={clsx(
                    'text-2xs font-mono px-2 py-0.5 rounded border transition-colors',
                    activeFilter === id
                      ? 'bg-bull/15 border-bull/40 text-bull'
                      : 'border-border text-ink-3 hover:text-ink-1 hover:border-border-strong'
                  )}>
                  {label} <span className="opacity-60">({count})</span>
                </button>
              ))}
            </div>
            {activeFilter !== 'all' && filteredGappers.length !== scanner.gappers.length && (
              <div className="text-2xs text-ink-3 font-mono">
                Showing {filteredGappers.length} of {scanner.gappers.length} gappers
              </div>
            )}
          </div>
        )}

        {/* Scan criteria pills — show when no results yet */}
        {scanner.gappers.length === 0 && (
          <div className="flex flex-wrap gap-1">
            <span className="badge badge-bull">Gap ≥3%</span>
            <span className="badge badge-bull">RVOL ≥1.5×</span>
            <span className="badge badge-amber">$10–$500</span>
          </div>
        )}

        {/* Last scan time */}
        {scanner.lastScanTime && !scanner.isScanning && (
          <div className="flex items-center justify-between text-2xs text-ink-3 font-mono">
            <span>
              Last scan: {new Date(scanner.lastScanTime).toLocaleTimeString('en-US', {
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                timeZone: 'America/New_York'
              })} ET
            </span>
            {scanner.gappers.length > 0 && (
              <span className="text-bull">{scanner.gappers.length} found</span>
            )}
          </div>
        )}
      </div>

      {/* Results header when we have gappers */}
      {scanner.gappers.length > 0 && (
        <div className="px-3 py-2 border-b border-border bg-bg-2/40">
          <div className="flex items-center justify-between mb-1">
            <span className="text-2xs text-ink-2 font-mono font-medium">
              {scanner.gappers.length} candidate{scanner.gappers.length !== 1 ? 's' : ''}
            </span>
            {qualCounts.loading > 0 && (
              <span className="text-2xs text-ink-3 font-mono flex items-center gap-1">
                <Loader2 size={9} className="animate-spin" />
                {qualCounts.loading} checking…
              </span>
            )}
          </div>

          {/* Catalyst + qual summary */}
          <div className="flex items-center gap-2 text-2xs font-mono mt-0.5 flex-wrap">
            {catalystCount > 0 && (
              <span className="flex items-center gap-1 text-bull">
                <span className="w-1.5 h-1.5 rounded-full bg-bull/60" />
                {catalystCount} catalyst{catalystCount !== 1 ? 's' : ''}
              </span>
            )}
            {catalystCount > 0 && (qualCounts.pass + qualCounts.warn + qualCounts.fail) > 0 && (
              <span className="text-ink-3">·</span>
            )}
            {(qualCounts.pass + qualCounts.warn + qualCounts.fail) > 0 && (
              <>
                {qualCounts.pass > 0 && <span className="text-bull">{qualCounts.pass} opt✓</span>}
                {qualCounts.warn > 0 && <span className="text-amber">{qualCounts.warn} opt⚠</span>}
                {qualCounts.fail > 0 && <span className="text-bear">{qualCounts.fail} opt✗</span>}
              </>
            )}
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>

      {/* Footer */}
      {scanner.gappers.length > 0 && (
        <div className="border-t border-border px-3 py-2 bg-bg-2/30 flex-shrink-0">
          <div className="text-2xs text-ink-3">
            Spread ≤$0.15 · OI ≥500 · Delta 0.25–0.45 · Budget fit
          </div>
          <div className="text-2xs text-ink-3 mt-0.5">
            Tap <ChevronDown className="inline w-3 h-3" /> on any row for options breakdown
          </div>
        </div>
      )}
    </aside>
  );
}
